import React, { useEffect, useState } from "react";
import { useAppSelector } from "~/store/hooks";
import * as selectors from "~/action-panel/components/import-module/components/import-wizard/selectors";
import { TemplateManagerGrid } from "~/admin/setup/template-manager/add-edit/components";
import { useIntl } from "react-intl";
import { Loader } from "~/core";
import {
    FieldBoundaryTemplate,
    ImportAttribute,
    SampleAttribute,
} from "~/admin/setup/template-manager/data/model";
import { useDispatch } from "react-redux";
import { constants } from "~/admin/setup/template-manager/data";
import { fetchDropdownData } from "~/core/dropdowns/actions";
import { TemplateAPI } from "@ai360/core";
import { getDropdowns } from "~/hocs/crud/selectors";
import { setImportColumnSelectionStore } from "~/action-panel/components/import-module/components/import-wizard/actions";
import "./field-boundary-columns.css";

interface IProps {
    canContinue: boolean;
    updateCanContinue: (value: boolean) => void;
}

export function FieldBoundaryColumns({ canContinue, updateCanContinue }: IProps) {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const columnSelection = useAppSelector(selectors.getImportColumnSelectionInfo);
    const importTypeGuid = useAppSelector(selectors.getImportWizardType)?.guid;
    const sampleAttributes: SampleAttribute[] =
        useAppSelector(getDropdowns)[constants.SAMPLE_ATTRIBUTES] || [];

    const [importAttributes, setImportAttributes] = useState(
        useAppSelector(selectors.getImportColumnSelectionStore)
    );

    const { standardGridData = [], standardColumnHeaders = [] } = columnSelection ?? {};

    const templateInfo = new FieldBoundaryTemplate({
        headerRow: 1,
        matchHeaderName: 1,
    });

    useEffect(() => {
        dispatch(
            fetchDropdownData({
                [constants.SAMPLE_ATTRIBUTES]: {
                    url: TemplateAPI.GET_SAMPLE_ATTRIBUTES_URL,
                    model: importTypeGuid,
                },
            })
        );

        updateCanContinue(importAttributes?.length > 0);
    }, []);

    const showLoader = !columnSelection || !sampleAttributes;

    const updateSelectedAttributes = (value: Partial<ImportAttribute>) => {
        let newSelection: ImportAttribute[];
        if (value.importAttributeGuid) {
            const column = standardColumnHeaders.find(
                (x) => x.importFileColumnName === value.columnNameInFile
            );
            const ia = new ImportAttribute(
                value.importAttributeGuid,
                value.columnNameInFile,
                value.columnOrderInFile,
                column.attributeGuid
            );
            ia.isShapeFile = value.isShapeFile;
            newSelection = [...importAttributes, ia];
        } else {
            newSelection = importAttributes.filter(
                (x) => x.columnNameInFile !== value.columnNameInFile
            );
        }

        dispatch(setImportColumnSelectionStore(newSelection));
        setImportAttributes(newSelection);
        if (newSelection.length > 0 !== canContinue) {
            updateCanContinue(!canContinue);
        }
    };

    return (
        <div className="field-boundary-columns">
            {showLoader ? (
                <Loader />
            ) : (
                <>
                    <div className="instructions">
                        Select correct column for Customer, Farm, and/or Field
                    </div>
                    <TemplateManagerGrid
                        className="column-select"
                        columns={standardColumnHeaders}
                        currentTab={1}
                        data={standardGridData}
                        fileMetadata={templateInfo}
                        formatMessage={formatMessage}
                        hideTopBar={true}
                        importAttributes={importAttributes}
                        sampleAttributes={sampleAttributes}
                        updateImportAttributes={(fullValue) =>
                            updateSelectedAttributes(fullValue.value)
                        }
                        updateImportTemplateList={() => null}
                    />
                </>
            )}
        </div>
    );
}
